import {
  AssessmentLayerTabUK,
  AssessmentLayerTabUS,
} from "react-migration/layouts/map/Multilayer/Bundle/Workbench/AssessmentTab/types";
import { LogEventProps, logEvent } from "./logEvent";
import {
  ManagementLayerTabUK,
  ManagementLayerTabUS,
} from "react-migration/layouts/map/Multilayer/Bundle/Workbench/ManagmentTab/types";

import { Position } from "geojson";
import { SearchFilterDTO } from "@landtechnologies/dtos";
import { SelectionType } from "src/js/stores/map/store";
import { TransactionPurchaseType } from "react-migration/domains/ownership/typings/Transaction";

/**
 * Type definitions for all Mixpanel events
 *
 * The goal here is to create a central reference to all events that the landinsight web app sends to mixpanel.
 *
 * *Why are there no enums for the event names? We are passing magic strings everywhere...*
 *
 *
 * We have decided to intentionally _avoid_ enum definitions here and use hardcoded strings for mixpanel event names
 *
 * Pros:
 * - Quicker searching when dealing with Product, who will ask questions such as "how does the 'Site Card Activity Tab - Comment Added' event work"
 * - You still have typesafety and autosuggestions around the app due to typescript.
 * - Bloats this events definition file significantly and doubles the maintenance burden/ complexity when adding events
 * - Prevents having lots of enum imports to maintain around the app which makes consumers more portable
 * - Prevents having to come up with a good camel case representation for every event and keeps you in the "Mixpanel" headspace
 * - Quicker to read and understand exactly which events are available in each area of the app rather than "Translating" from enums
 *
 * Cons:
 * - You can no longer change the event name in one place. Our argument here is that these will rarely change and if they do have to change there will be very few consumers so a global find and replace will be a lightweight and quick solution.
 *
 * Related Articles
 * (Duplicate events in mixpanel article)[https://www.notion.so/landtech/Double-Trouble-MixPanel-Madness-257a13dae8c44654bad336c637275501#0a0e3544be104f988b2c305b84f08c4c]
 */

type LeftClickProps = {
  clickMethod: "left";
  mapLatLng: Position;
  mapZoom: number;
  selectionLayerId: string;
  selectionType: SelectionType;
  activeLayers?: string[];
  exploreVersion: "v1" | "v2";
} & LogEventProps;

type RightClickProps = {
  clickMethod: "right";
} & (
  | {
      action: "open";
      mapLatLng: Position;
    }
  | {
      action: "exit";
    }
  | {
      action: "select";
      layerType: string;
      id: string;
    }
) & { exploreVersion: "v1" | "v2" };

type ClusterClickProps = {
  clickMethod: "left";
  action: "cluster_click";
  selectionLayerId: string;
  zoom: number;
  clusterAddress: string;
} & LogEventProps;

type ClickProps = LeftClickProps | RightClickProps | ClusterClickProps;

export type UpgradeEventLocation =
  | AssessmentLayerTabUS
  | AssessmentLayerTabUK
  | ManagementLayerTabUS
  | ManagementLayerTabUK
  | "Selection"
  | "BundleExplorer"
  | "Navbar"
  | "UserMenu"
  | "TrialEndingModal"
  | "SelfServiceUpgrade"
  | "QuickInsights"
  | "Permits"
  | "ContactSupportUpgrade";

export type LogEventCreatedSiteEventLocation =
  | "Workbench"
  | "SiteSourcing"
  | "PlanningSearch"
  | "OwnershipSearch"
  | "PropertySearch"
  | "PlanningResultActions"
  | "SelectionView";

export enum WorkbenchEventName {
  WORKBENCH_GET_STARTED_OVERLAY_OPENED = "Workbench Get Started - Overlay Opened",
  WORKBENCH_GET_STARTED_OVERLAY_CLOSED = "Workbench Get Started - Overlay Closed",
  WORKBENCH_GET_STARTED_OVERLAY_ASSESS_CLICKED = "Workbench Get Started - Overlay Assess Clicked",
  WORKBENCH_GET_STARTED_OVERLAY_UPLOAD_DOCUMENT_CLICKED = "Workbench Get Started - Overlay Upload Document Clicked",
  WORKBENCH_GET_STARTED_OVERLAY_COMMENT_CLICKED = "Workbench Get Started - Overlay Comment Clicked",
  WORKBENCH_GET_STARTED_OVERLAY_CREATE_REPORT_CLICKED = "Workbench Get Started - Overlay Create Report Clicked",
  WORKBENCH_GET_STARTED_OVERLAY_EXPORT_BOUNDARY_CLICKED = "Workbench Get Started - Overlay Export Boundary Clicked",
  WORKBENCH_QUICK_ACTIONS_CREATE_LETTER_CLICK = "Workbench Quick Actions - Create Letter Clicked",
  WORKBENCH_QUICK_ACTIONS_UPLOAD_FILE_CLICK = "Workbench Quick Actions - Upload File Clicked",
  WORKBENCH_QUICK_ACTIONS_CREATE_REPORT_CLICK = "Workbench Quick Actions - Create Report Clicked",
  WORKBENCH_QUICK_ACTIONS_VIEW_ALL_REPORTS_CLICK = "Workbench Quick Actions - View All Reports Clicked",
  WORKBENCH_QUICK_ACTIONS_SITE_REPORT_BY_LANDTECH_CLICK = "Workbench Quick Actions - Site Report by LandTech Clicked",
  WORKBENCH_QUICK_ACTIONS_APPRAISAL_TOOL_CLICK = "Workbench Quick Actions - Appraisal Tool by LandTech Clicked",
  WORKBENCH_QUICK_ACTIONS_RENKAP_REPORT_CLICK = "Workbench Quick Actions - Renkap Report Clicked",
  WORKBENCH_QUICK_ACTIONS_ATKINS_REPORT_CLICK = "Workbench Quick Actions - Atkins Report Clicked",
  WORKBENCH_QUICK_ACTIONS_AIDASH_REPORT_CLICK = "Workbench Quick Actions - Aidash Report Clicked",

  WORKBENCH_QUICK_PURCHASE_BULK_TITLES_CLICK = "Workbench Quick Actions - Purchase Bulk Titles Clicked",
  WORKBENCH_MANAGEMENT_LETTERS_TAB_CREATE_LETTER_CLICK = "Workbench Management - Letters Tab - Create Letter Clicked",
  WORKBENCH_MANAGEMENT_LETTERS_TAB_DOWNLOAD_LETTER = "Workbench Management - Letters Tab - Download Letter",
  WORKBENCH_MANAGEMENT_LETTERS_TAB_ACCESS_GRANTED = "Workbench Management - Letters Tab - Access Granted",
  WORKBENCH_MANAGEMENT_LETTERS_TAB_ACCESS_DENIED = "Workbench Management - Letters Tab - Access Denied",
  WORKBENCH_MANAGEMENT_ACTIVITIES_TAB_COMMENT_ADDED = "Workbench Management - Activities Tab - Comment Added",
  WORKBENCH_MANAGEMENT_ACTIVITIES_TAB_COMMENT_DELETED = "Workbench Management - Activities Tab - Comment Deleted",
  WORKBENCH_MANAGEMENT_ACTIVITIES_TAB_COMMENT_PINNED = "Workbench Management - Activities Tab - Comment Pinned",
  WORKBENCH_MANAGEMENT_ACTIVITIES_TAB_COMMENT_EDITED = "Workbench Management - Activities Tab - Comment Edited",
  WORKBENCH_MANAGEMENT_ACTIVITIES_TAB_ATTACHMENT_PINNED = "Workbench Management - Activities Tab - Attachment Pinned",
  WORKBENCH_MANAGEMENT_ACTIVITIES_TAB_TIDBIT_PINNED = "Workbench Management - Activities Tab - Tidbit Pinned",
  WORKBENCH_MANAGEMENT_FILES_TAB_FILE_DROPPED = "Workbench Management - Files Tab - File Dropped",
  WORKBENCH_MANAGEMENT_FILES_TAB_FILE_UPLOADED = "Workbench Management - Files Tab - File Uploaded",
  WORKBENCH_MANAGEMENT_FILES_TAB_FILE_DELETED = "Workbench Management - Files Tab - File Deleted",
  WORKBENCH_MANAGEMENT_FILES_TAB_FILE_PINNED = "Workbench Management - Files Tab - File Pinned",
  WORKBENCH_MANAGEMENT_FILES_TAB_FILE_UNPINNED = "Workbench Management - Files Tab - File Unpinned",
  WORKBENCH_CARD_CLICK = "Workbench card click",
  WORKBENCH_CARD_EXPANDED = "Workbench card expanded",
  WORKBENCH_FILTER_CLICK = "Workbench filter click",
  WORKBENCH_TAB_CLICK = "Workbench tab click",
}

export enum SiteReportEventName {
  SITE_REPORT_SAVE_AS_PDF_CLICKED = "Site Report - Save As PDF - clicked",
  SITE_REPORT_DOWNLOADED = "Site report downloaded",
}

export enum PublicSiteEventName {
  PUBLIC_SITE_SIGN_UP_CLICKED = "Public site - Sign up clicked",
  PUBLIC_SITE_START_TRIAL_CLICKED = "Public site - Start trial clicked",
  PUBLIC_SITE_ALREADY_HAVE_ACCOUNT_CLICKED = "Public site - Already have an account clicked",
}

export const enum ProjectEventName {
  PROJECTS_TABLE_PROJECT_SELECTED = "My projects table - Project selected",
  PROJECTS_TABLE_PROJECT_ARCHIVED = "My projects table - Project archived",
  ARCHIVED_PROJECTS_TABLE_PROJECT_RESTORED = "Archived projects table - Project restored",
  RECENT_PROJECTS_PROJECT_SELECTED = "Recent projects - Project selected",
  PROJECT_DROPDOWN_PROJECT_SELECTED = "Project dropdown - Project selected",
}

export type LogEventV2Props =
  | {
      name: "Location Search";
      properties: {
        type: "adressess" | "titlesUk" | "parcelsUs" | "sites";
        recentSearch: boolean;
        id: string;
        country: string;
        lat?: number;
        lng?: number;
        location?: string;
        location_shortname?: string;
        siteName?: string;
      };
    }
  | {
      name: "Isochrone Layer Map click";
      properties: {
        lng: string;
        lat: string;
        zoom: number;
        routingType: string;
        routingTime: string;
      };
    }
  | {
      name: WorkbenchEventName.WORKBENCH_TAB_CLICK;
      properties: {
        tabName: string;
      };
    }
  | {
      name: WorkbenchEventName.WORKBENCH_CARD_CLICK;
      properties: {
        cardName: string;
        action: string;
        clickDetail: string;
        planningApplicationID?: string;
        planningApplicationClassification?: string;
        planningApplicationType?: string;
        insightSummary?: boolean;
        uprn?: string;
        compsHometrackEnabled?: boolean;
        url?: string;
        actionUrl?: string;
      };
    }
  | {
      name: "Workbench selection clear";
      properties: Record<string, never>;
    }
  | {
      name: "Workbench card expanded";
      properties: {
        state: boolean;
        cardName: string;
        clickDetail?: string;
      };
    }
  | {
      name: "Workbench expanded";
      properties: {
        state: boolean;
      };
    }
  | {
      name: "Workbench expanded mobile";
      properties: {
        state: boolean;
      };
    }
  | {
      name: "Zoom to site";
      properties: {
        name: string;
      };
    }
  | {
      name: "Navigation";
      properties: {
        route: string;
        redirect?: string;
      };
    }
  | {
      name: "Designations list filter";
      properties: {
        text: string;
      };
    }
  | {
      name: "Bundle Map Click";
      properties: ClickProps;
    }
  | {
      name: "Selection Summary - KitKat Toggle";
      properties: { fingerId: string; expanded: boolean };
    }
  | {
      name: "Created site";
      properties: {
        /** The active map bundle ID when the saving was triggered */
        source: string;
        /** Where the saving was triggered from */
        eventLocation: LogEventCreatedSiteEventLocation;
        /** If the saving was triggered from a selection, what was it's type */
        selectionType?: string;
        zoom?: number;
        idToSave?: string;
        mapType?: string;
        searchId?: string;
        sitesSavedCount?: number;
        /** @deprecated only used for legacy ownership search */
        batch?: {
          from: number;
          to: number;
        };
        /** Name of the card that triggered the site creation */
        cardName?: string;
      };
    }
  | {
      name: "Toggle layer";
      properties: {
        bundleId: string;
        layerId: string;
        state: boolean;
        onlyThisLayer?: boolean;
        exploreVersion: "v1" | "v2";
      };
    }
  | {
      name: "Sites layer button click";
      properties: {
        type: "share" | "edit" | "save" | "update" | "delete";
      };
    }
  | {
      name: "Intersections click";
      properties: {
        layerId: string;
      };
    }
  | {
      name: "Ownership Views Run Out";
      properties: {
        views: number;
        cap: number;
        hasHitCap: boolean;
      };
    }
  | {
      name: "Selection Detail - Back Button Click";
      properties: {
        detailSelectionType?: string;
        detailSelectionId?: string;
      };
    }
  | {
      name: "Planning authority layer filtering";
      properties: {
        presumptionInFavour: boolean;
      };
    }
  | {
      name: "Open Layer Controls — No Considerations CTA";
      properties: Record<string, never>;
    }
  | {
      name: "Sites Uploader - Opened";
      properties: { view: "stages" | "list" };
    }
  | {
      name: "Sites Uploader - Closed";
      properties?: undefined;
    }
  | {
      name: "Sites Uploader - File Dropped";
      properties: {
        types: string[];
        sizes: string[];
        status: "success" | "error";
        errors?: string[];
      };
    }
  | {
      name: "Sites Uploader - Upload Confirmed";
      properties?: undefined;
    }
  | {
      name: "Sites Uploader - Upload Completed";
      properties: {
        status: "success" | "error";
        numberOfUploadedFiles?: number;
        numberOfFeatureInputs?: number;
        numberOfNewSites?: number;
        uploadId?: string;
        errors?: string[];
      };
    }
  | {
      name: "Sites Uploader - Saving Sites Failed";
      properties: { message: string };
    }
  | {
      name: WorkbenchEventName.WORKBENCH_MANAGEMENT_FILES_TAB_FILE_DROPPED;
      properties: {
        types: string[];
        sizes: string[];
        status: "success" | "error";
        errors?: string[];
      };
    }
  | {
      name: WorkbenchEventName.WORKBENCH_MANAGEMENT_FILES_TAB_FILE_UPLOADED;
      properties: {
        fileName: string;
        fileSize: number;
      };
    }
  | {
      name: WorkbenchEventName.WORKBENCH_MANAGEMENT_FILES_TAB_FILE_DELETED;
      properties: {
        fileId: string;
        fileName?: string;
      };
    }
  | {
      name: WorkbenchEventName.WORKBENCH_MANAGEMENT_FILES_TAB_FILE_PINNED;
      properties: {
        fileName: string;
        isPinned: boolean;
      };
    }
  | {
      name: WorkbenchEventName.WORKBENCH_MANAGEMENT_FILES_TAB_FILE_UNPINNED;
      properties: {
        fileName: string;
        isPinned: boolean;
      };
    }
  | {
      name: "Layers - Individual layer filter click";
      properties: {
        filterName: string;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        filterState: any;
        layerId: string;
      };
    }
  | {
      name: "drawings expanded";
      properties: {
        state: boolean;
      };
    }
  | {
      name: "Select bundle";
      properties: {
        bundleId: string;
      };
    }
  | {
      name: "drawings visibility";
      properties: {
        state: boolean;
      };
    }
  | {
      name: "land assembly enabled";
      properties: {
        state: boolean;
      };
    }
  | { name: "Ownership Search: Company Tree View"; properties: { companyNumber: string } }
  | {
      name: "Ownership Search: Company Tree Expand";
      properties: { expanded: boolean; companyNumber: string };
    }
  | {
      name: "Expand layer";
      properties: {
        bundleId: string;
        layerId: string;
        state: "expand" | "collapse";
      };
    }
  | {
      name: "Explorer layer opened";
      properties: {
        bundleId: string;
        layerId: string;
        state: "expand" | "collapse";
      };
    }
  | {
      name: "Feature vote";
      properties: {
        featureName: string;
        mapLatLng?: Position;
        selectionType?: SelectionType;
        selectionId?: string;
        vote: "Thumbs Up" | "Thumbs Down";
      };
    }
  | { name: "Explorer tab click"; properties: { tabId: "layers" | "search" } }
  | { name: "Explorer expanded"; properties: { state: boolean } }
  | {
      name: "Explorer expanded mobile";
      properties: {
        state: boolean;
      };
    }
  | { name: "Explorer search redirect"; properties: { layerSearch: string; type: "site-sourcing" | "location-search" } }
  | { name: "Zoom to location"; properties: { state: string; errorMessage: string } }
  | {
      name: SiteReportEventName.SITE_REPORT_DOWNLOADED;
      properties: {
        addedLogo: boolean;
        sectionsToggled: string[];
        planningRemoved: boolean;
        compsRemoved: boolean;
        siteId: string;
        siteAreaInMeters: number;
      };
    }
  | {
      name: SiteReportEventName.SITE_REPORT_SAVE_AS_PDF_CLICKED;
      properties: {
        siteId: string;
      };
    }
  | {
      name: "Quick action click";
      properties: {
        source: string;
        action?: string;
      };
    }
  | {
      name: "Upgrade";
      properties: {
        source: UpgradeEventLocation;
        action: "initiate-upgrade" | "upgrade" | "downgrade" | "premium-upgrade" | "contact-support" | "dismiss";
        currentLicense?: string;
        selectedLicense?: string;
      };
    }
  | {
      name: WorkbenchEventName.WORKBENCH_FILTER_CLICK;
      properties: {
        cardName: string;
        filterName: string;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        filterState: any;
      };
    }
  | {
      name: "Explorer search";
      properties: {
        action: "clear" | "search";
        value?: string;
      };
    }
  | {
      name: "Workbench personalisation";
      properties: {
        cardName: "insights";
        action: "Amend order" | "Toggle enabled state";
        name: string;
        enabled?: boolean;
        value?: number;
        previousValue?: number;
        order?: string[];
        enabledList?: string[];
        disabledList?: string[];
        enabledCount?: number;
        disabledCount?: number;
      };
    }
  | {
      name: WorkbenchEventName.WORKBENCH_GET_STARTED_OVERLAY_OPENED;
      properties: {
        siteId: string;
      };
    }
  | {
      name: WorkbenchEventName.WORKBENCH_GET_STARTED_OVERLAY_CLOSED;
      properties: {
        siteId: string;
      };
    }
  | {
      name: WorkbenchEventName.WORKBENCH_GET_STARTED_OVERLAY_ASSESS_CLICKED;
      properties: {
        siteId: string;
      };
    }
  | {
      name: WorkbenchEventName.WORKBENCH_GET_STARTED_OVERLAY_UPLOAD_DOCUMENT_CLICKED;
      properties: {
        siteId: string;
      };
    }
  | {
      name: WorkbenchEventName.WORKBENCH_GET_STARTED_OVERLAY_COMMENT_CLICKED;
      properties: {
        siteId: string;
      };
    }
  | {
      name: WorkbenchEventName.WORKBENCH_GET_STARTED_OVERLAY_CREATE_REPORT_CLICKED;
      properties: {
        siteId: string;
      };
    }
  | {
      name: WorkbenchEventName.WORKBENCH_GET_STARTED_OVERLAY_EXPORT_BOUNDARY_CLICKED;
      properties: {
        siteId: string;
        fileFormat: string;
      };
    }
  | {
      name: WorkbenchEventName.WORKBENCH_MANAGEMENT_LETTERS_TAB_CREATE_LETTER_CLICK;
      properties: {
        siteId: string;
      };
    }
  | {
      name: WorkbenchEventName.WORKBENCH_QUICK_ACTIONS_CREATE_LETTER_CLICK;
      properties: {
        siteId: string;
      };
    }
  | {
      name: WorkbenchEventName.WORKBENCH_MANAGEMENT_LETTERS_TAB_ACCESS_GRANTED;
      properties: {
        siteId: string;
      };
    }
  | {
      name: WorkbenchEventName.WORKBENCH_MANAGEMENT_LETTERS_TAB_ACCESS_DENIED;
      properties: {
        siteId: string;
        reason: string;
      };
    }
  | {
      name: WorkbenchEventName.WORKBENCH_MANAGEMENT_LETTERS_TAB_DOWNLOAD_LETTER;
      properties: {
        siteLetterId: string;
      };
    }
  | {
      name: WorkbenchEventName.WORKBENCH_MANAGEMENT_ACTIVITIES_TAB_COMMENT_ADDED;
      properties: {
        commentId: string;
      };
    }
  | {
      name: WorkbenchEventName.WORKBENCH_MANAGEMENT_ACTIVITIES_TAB_COMMENT_DELETED;
      properties: {
        commentId: string;
      };
    }
  | {
      name: WorkbenchEventName.WORKBENCH_MANAGEMENT_ACTIVITIES_TAB_COMMENT_PINNED;
      properties: {
        commentId: string;
        isPinned: boolean;
      };
    }
  | {
      name: WorkbenchEventName.WORKBENCH_MANAGEMENT_ACTIVITIES_TAB_COMMENT_EDITED;
      properties: {
        commentId: string;
      };
    }
  | {
      name: WorkbenchEventName.WORKBENCH_MANAGEMENT_ACTIVITIES_TAB_ATTACHMENT_PINNED;
      properties: {
        attachmentId: string;
        isPinned: boolean;
      };
    }
  | {
      name: WorkbenchEventName.WORKBENCH_MANAGEMENT_ACTIVITIES_TAB_TIDBIT_PINNED;
      properties: {
        tidbitId: string;
        isPinned: boolean;
      };
    }
  | {
      name: WorkbenchEventName.WORKBENCH_QUICK_ACTIONS_UPLOAD_FILE_CLICK;
      properties: {
        siteId: string;
      };
    }
  | {
      name: "Sites Pipeline - Export site boundaries";
      properties: {
        fileType: "geojson" | "shp" | "kml";
        count: number;
      };
    }
  | {
      name: WorkbenchEventName.WORKBENCH_QUICK_ACTIONS_CREATE_REPORT_CLICK;
      properties: {
        siteId: string;
      };
    }
  | {
      name: WorkbenchEventName.WORKBENCH_QUICK_ACTIONS_VIEW_ALL_REPORTS_CLICK;
      properties: {
        siteId: string;
      };
    }
  | {
      name: WorkbenchEventName.WORKBENCH_QUICK_ACTIONS_APPRAISAL_TOOL_CLICK;
      properties: {
        siteId: string;
      };
    }
  | {
      name: WorkbenchEventName.WORKBENCH_QUICK_ACTIONS_RENKAP_REPORT_CLICK;
      properties: {
        siteId: string;
      };
    }
  | {
      name: WorkbenchEventName.WORKBENCH_QUICK_ACTIONS_ATKINS_REPORT_CLICK;
      properties: {
        siteId: string;
      };
    }
  | {
      name: WorkbenchEventName.WORKBENCH_QUICK_ACTIONS_AIDASH_REPORT_CLICK;
      properties: {
        siteId: string;
      };
    }
  | {
      name: WorkbenchEventName.WORKBENCH_QUICK_PURCHASE_BULK_TITLES_CLICK;
      properties: {
        siteId: string;
        type: TransactionPurchaseType;
        ownerType: "all" | "unknown";
        selection: "all" | "freeholders" | "leaseholders";
      };
    }
  | {
      name: "Completed billing address";
      properties: Record<string, never>;
    }
  | {
      name: "Clicked on greyed out layer";
      properties: {
        layer: string;
      };
    }
  | {
      name: "Site Sourcing - AI Filters Added";
      properties: {
        readonly filters: SearchFilterDTO[];
        reason: string;
        locationFilterReason: string;
        filterNotAvailable: boolean;
        bestLocationMatch: {
          type: "location" | "postcode" | "council";
          match: string;
          distance?: number;
          score: number;
        };
        prompt: string;
      };
    }
  | {
      name: "Shared site";
      properties: {
        action: "row_click" | "export_boundary" | "view_on_map" | "create_report";
        error?: string;
        fileType?: string;
        siteId?: string;
        state?: string;
      };
    }
  | {
      name: "Share site";
      properties: {
        action: "copy_link" | "grant_access" | "update_access" | "revoke_access" | "resend_invitation" | "open_modal";
        email?: string;
        error?: boolean;
        siteId?: string;
      };
    }
  | {
      name: "Store opened";
      properties: {
        clickSource: string;
      };
    }
  | {
      name: "AiDash Report Sidebar closed";
      properties: {
        siteId: string;
      };
    }
  | {
      name: "On demand report - overview opened";
      properties: {
        partner: string;
        topUpRequired: boolean;
        currentUserBalance: number;
        reportValue: number;
      };
    }
  | {
      name: "On demand report - sample report click";
      properties: {
        providerName: string;
      };
    }
  | {
      name: "On demand report - purchase button click";
      properties: {
        providerName: string;
        topUpRequired: boolean;
        topUpMinimumValueRequired: number;
      };
    }
  | {
      name: "Purchase confirmation button click";
      properties: {
        providerName: string;
        productName: string;
        siteName: string;
        orderAmount: number;
        type: string;
      };
    }
  | {
      name: "Purchase successful";
      properties: {
        providerName: string;
        productName: string;
        siteName: string;
        orderAmount: number;
        type: string;
      };
    }
  | {
      name: "Explorer reset layers";
      properties: Record<string, never>;
    }
  | {
      name: "Explorer display active layers only";
      properties: {
        state: boolean;
      };
    }
  | {
      name: "Explorer group expanded";
      properties: {
        state: boolean;
        groupName: string;
      };
    }
  | {
      name: "Beta toggle";
      properties: {
        betaName: "explorerv2";
        state: boolean;
      };
    }
  | {
      name: PublicSiteEventName.PUBLIC_SITE_SIGN_UP_CLICKED;
      properties: {
        shareId?: string;
        type?: string;
      };
    }
  | {
      name: PublicSiteEventName.PUBLIC_SITE_START_TRIAL_CLICKED;
      properties: {
        shareId?: string;
        type?: string;
      };
    }
  | {
      name: PublicSiteEventName.PUBLIC_SITE_ALREADY_HAVE_ACCOUNT_CLICKED;
      properties: {
        shareId?: string;
        type?: string;
      };
    }
  | {
      name: ProjectEventName.PROJECTS_TABLE_PROJECT_SELECTED;
      properties: {
        projectId: string;
      };
    }
  | {
      name: ProjectEventName.PROJECTS_TABLE_PROJECT_ARCHIVED;
      properties: {
        projectId: string;
      };
    }
  | {
      name: ProjectEventName.ARCHIVED_PROJECTS_TABLE_PROJECT_RESTORED;
      properties: {
        projectId: string;
      };
    }
  | {
      name: ProjectEventName.RECENT_PROJECTS_PROJECT_SELECTED;
      properties: {
        projectId: string;
      };
    }
  | {
      name: ProjectEventName.PROJECT_DROPDOWN_PROJECT_SELECTED;
      properties: {
        projectId: string;
      };
    };

export const logEventV2 = ({ name, properties }: LogEventV2Props) => logEvent(name, properties);
